import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Joyride from 'react-joyride';
import {
  Eye,
  MousePointer,
  Percent,
  DollarSign,
  Target,
  TrendingUp,
  Wallet,
  PiggyBank,
  RefreshCw,
  Clock,
  Plus,
  FileText,
  Users,
  ChevronRight,
  Search,
  Award,
  HelpCircle,
  Download,
  ChevronDown,
  Check,
  X,
  BarChart3,
  Megaphone,
  Key,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { dashboardService, DashboardStats, ChartData, Insight, TopSearchTerm, TopCampaign, TopSpendingCampaign, Activity, ScheduledPost, DateRange } from '../../services/dashboardService';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import KPICard from '../../components/dashboard/KPICard';
import PerformanceChart from '../../components/dashboard/PerformanceChart';
import PlatformComparison from '../../components/dashboard/PlatformComparison';
import AIInsightsPanel from '../../components/dashboard/AIInsightsPanel';
import CommandBar from '../../components/dashboard/CommandBar';
import ConnectAccountsHero from '../../components/dashboard/ConnectAccountsHero';
import QuickActionsGrid from '../../components/dashboard/QuickActionsGrid';
import ActivityFeed from '../../components/dashboard/ActivityFeed';
import UpcomingPosts from '../../components/dashboard/UpcomingPosts';
import { useDashboardTour } from '../../hooks/useDashboardTour';
import WelcomeModal from '../../components/onboarding/WelcomeModal';

const getDefaultDateRange = (): DateRange => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0]
  };
};

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, plan } = useAuth();
  const { projects, currentProject } = useProject();
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showValues, setShowValues] = useState(true);
  const { run: tourRun, steps: tourSteps, stepIndex: tourStepIndex, handleCallback: tourCallback, restartTour, showWelcome, startTourFromWelcome, skipWelcome } = useDashboardTour();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [topSpendingToday, setTopSpendingToday] = useState<TopSpendingCampaign[]>([]);
  const [topSearchTerms, setTopSearchTerms] = useState<TopSearchTerm[]>([]);
  const [topCampaigns, setTopCampaigns] = useState<TopCampaign[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [upcomingPosts, setUpcomingPosts] = useState<ScheduledPost[]>([]);

  // Preset helpers
  const applyPreset = (preset: string) => {
    const now = new Date();
    const end = now.toISOString().split('T')[0];
    let start: Date;

    switch (preset) {
      case '7d':
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start = new Date(now);
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start = new Date(now);
        start.setDate(start.getDate() - 90);
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: lastDay.toISOString().split('T')[0] });
        return;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        start = new Date(now);
        start.setDate(start.getDate() - 30);
    }
    setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end });
  };

  // Export handler
  const handleExport = async (format: 'pdf' | 'xlsx' | 'csv') => {
    try {
      setShowExportMenu(false);
      const blob = await dashboardService.exportReport(format, dateRange);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'xlsx' ? 'xlsx' : format;
      a.download = `report_${dateRange.startDate}_${dateRange.endDate}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  // Dashboard PDF report handler
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [showReportSectionsModal, setShowReportSectionsModal] = useState(false);

  // Report sections configuration
  const reportSections = [
    { key: 'kpis', label: t('dashboard.reportSections.kpis', 'Resumo de KPIs'), description: t('dashboard.reportSections.kpisDesc', 'Impressões, Cliques, CTR, Conversões, Investimento, Receita, ROAS e ROI'), icon: BarChart3 },
    { key: 'campaigns', label: t('dashboard.reportSections.campaigns', 'Performance por Campanha'), description: t('dashboard.reportSections.campaignsDesc', 'Tabela com métricas detalhadas de cada campanha'), icon: Megaphone },
    { key: 'keywords', label: t('dashboard.reportSections.keywords', 'Top Keywords'), description: t('dashboard.reportSections.keywordsDesc', 'Ranking de palavras-chave com posição, volume e dificuldade'), icon: Key },
  ];

  const [selectedSections, setSelectedSections] = useState<string[]>(reportSections.map(s => s.key));

  const allSectionsSelected = selectedSections.length === reportSections.length;

  const toggleSection = (key: string) => {
    setSelectedSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleAllSections = () => {
    if (allSectionsSelected) {
      setSelectedSections([]);
    } else {
      setSelectedSections(reportSections.map(s => s.key));
    }
  };

  const handleExportDashboardPDF = async () => {
    if (selectedSections.length === 0) {
      toast.error(t('dashboard.reportSections.selectAtLeastOne', 'Selecione pelo menos uma seção para o relatório.'));
      return;
    }
    try {
      setShowReportSectionsModal(false);
      setIsExportingPDF(true);
      toast.loading(t('dashboard.generatingPDF', 'Gerando relatório PDF...'), { id: 'pdf-export' });
      const blob = await dashboardService.exportDashboardPDF(dateRange, resolvedProjectId, selectedSections);
      if (blob.size < 100) {
        throw new Error('PDF vazio ou erro no servidor');
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard_report_${dateRange.startDate}_${dateRange.endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(t('dashboard.pdfReady', 'Relatório PDF gerado com sucesso!'), { id: 'pdf-export' });
    } catch (error) {
      console.error('Dashboard PDF export error:', error);
      toast.error(t('dashboard.pdfError', 'Erro ao gerar relatório PDF. Tente novamente.'), { id: 'pdf-export' });
    } finally {
      setIsExportingPDF(false);
    }
  };

  // Resolve which project ID(s) to pass to the API
  const activeProjects = projects.filter(p => p.is_active);
  const resolvedProjectId: string | string[] | null =
    selectedProjectIds.length === 0 ? 'all' :
    selectedProjectIds.length === 1 ? selectedProjectIds[0] :
    selectedProjectIds;

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dashboardService.getAllData(dateRange, resolvedProjectId);
      setStats(data.stats);
      setChartData(data.charts);
      setInsights(data.insights);
      setTopSpendingToday(data.topSpendingToday);
      setTopSearchTerms(data.topSearchTerms);
      setTopCampaigns(data.topCampaigns);
      setActivities(data.activities);
      setUpcomingPosts(data.upcomingPosts);
      setLastUpdated(data.lastUpdated);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setStats(null);
      setChartData(null);
      setInsights([]);
      setTopSpendingToday([]);
      setTopSearchTerms([]);
      setTopCampaigns([]);
      setActivities([]);
      setUpcomingPosts([]);
      setLastUpdated(null);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, JSON.stringify(resolvedProjectId)]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // KPI Cards configuration
  const kpiCards = stats ? [
    {
      title: t('dashboard.totalImpressions'),
      value: stats.impressions.value,
      change: stats.impressions.change,
      sparkline: stats.impressions.sparkline,
      icon: <Eye className="h-5 w-5 text-blue-600" />,
      format: 'number' as const
    },
    {
      title: t('dashboard.totalClicks'),
      value: stats.clicks.value,
      change: stats.clicks.change,
      sparkline: stats.clicks.sparkline,
      icon: <MousePointer className="h-5 w-5 text-green-600" />,
      format: 'number' as const
    },
    {
      title: t('dashboard.ctr'),
      value: stats.ctr.value,
      change: stats.ctr.change,
      sparkline: stats.ctr.sparkline,
      icon: <Percent className="h-5 w-5 text-purple-600" />,
      format: 'percentage' as const
    },
    {
      title: t('dashboard.cpc'),
      value: stats.cpc.value,
      change: stats.cpc.change,
      sparkline: stats.cpc.sparkline,
      icon: <DollarSign className="h-5 w-5 text-yellow-600" />,
      format: 'currency' as const
    },
    {
      title: t('dashboard.totalConversions'),
      value: stats.conversions.value,
      change: stats.conversions.change,
      sparkline: stats.conversions.sparkline,
      icon: <Target className="h-5 w-5 text-red-600" />,
      format: 'number' as const
    },
    {
      title: t('dashboard.roas'),
      value: stats.roas.value,
      change: stats.roas.change,
      sparkline: stats.roas.sparkline,
      icon: <TrendingUp className="h-5 w-5 text-teal-600" />,
      format: 'number' as const
    },
    {
      title: t('dashboard.totalSpend'),
      value: stats.spend.value,
      change: stats.spend.change,
      sparkline: stats.spend.sparkline,
      icon: <Wallet className="h-5 w-5 text-orange-600" />,
      format: 'currency' as const
    },
    {
      title: t('dashboard.roi'),
      value: stats.roi.value,
      change: stats.roi.change,
      sparkline: stats.roi.sparkline,
      icon: <PiggyBank className="h-5 w-5 text-emerald-600" />,
      format: 'percentage' as const
    }
  ] : [];

  // Quick actions
  const quickActions = [
    { title: t('dashboard.createCampaign'), icon: Plus, href: '/campaigns/new', color: 'bg-primary-600' },
    { title: t('dashboard.generateContent'), icon: FileText, href: '/content/generate', color: 'bg-secondary-600' },
    { title: t('dashboard.addCompetitor'), icon: Users, href: '/competitors', color: 'bg-green-600' },
    { title: t('dashboard.exportReport'), icon: Download, href: '#', color: 'bg-orange-600' }
  ];

  // Format timestamp
  const formatLastUpdated = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  // Preset definitions
  const presets = [
    { key: '7d', label: t('reports.periods.last7days') },
    { key: '30d', label: t('reports.periods.last30days') },
    { key: '90d', label: t('reports.periods.last90days') },
    { key: 'thisMonth', label: t('reports.periods.thisMonth') },
    { key: 'lastMonth', label: t('reports.periods.lastMonth') },
    { key: 'thisYear', label: t('reports.periods.thisYear') },
  ];

  // Determine active preset
  const getActivePreset = (): string | null => {
    const now = new Date();
    const end = now.toISOString().split('T')[0];
    const { startDate: s, endDate: e } = dateRange;

    if (e !== end) {
      // Check lastMonth
      const lmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lmEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      if (s === lmStart.toISOString().split('T')[0] && e === lmEnd.toISOString().split('T')[0]) return 'lastMonth';
      return null;
    }

    for (const days of [7, 30, 90]) {
      const d = new Date(now);
      d.setDate(d.getDate() - days);
      if (s === d.toISOString().split('T')[0]) return `${days}d`;
    }

    const tmStart = new Date(now.getFullYear(), now.getMonth(), 1);
    if (s === tmStart.toISOString().split('T')[0]) return 'thisMonth';

    const tyStart = new Date(now.getFullYear(), 0, 1);
    if (s === tyStart.toISOString().split('T')[0]) return 'thisYear';

    return null;
  };

  const activePreset = getActivePreset();

  return (
    <div className="space-y-6">
      {/* Welcome Modal */}
      <WelcomeModal
        open={showWelcome}
        onStartTour={startTourFromWelcome}
        onSkip={skipWelcome}
      />

      {/* Dashboard Tour */}
      <Joyride
        steps={tourSteps}
        run={tourRun}
        stepIndex={tourStepIndex}
        callback={tourCallback}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableOverlayClose
        spotlightClicks
        locale={{
          back: t('tour.back', 'Back'),
          close: t('tour.finish', 'Finish'),
          last: t('tour.finish', 'Finish'),
          next: t('tour.next', 'Next'),
          skip: t('tour.skip', 'Skip'),
        }}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#7c3aed',
            overlayColor: 'rgba(0, 0, 0, 0.6)',
          },
          tooltip: {
            borderRadius: '12px',
            padding: '20px',
          },
          tooltipTitle: {
            fontSize: '16px',
            fontWeight: 700,
          },
          tooltipContent: {
            fontSize: '14px',
          },
          buttonNext: {
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '13px',
          },
          buttonBack: {
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '13px',
          },
          buttonSkip: {
            fontSize: '13px',
          },
        }}
      />

      {/* Command Bar Modal */}
      <CommandBar />

      {/* Connect Accounts Wizard — substitui o bloco antigo de ações IA.
          Explica ao usuário o que cada conexão desbloqueia, mostra progresso e
          deep-link pra /settings. Sempre renderiza (mesmo com tudo conectado
          mostra 100% + plataformas com check). */}
      <div data-tour="ai-assistant">
        <ConnectAccountsHero
          userName={user?.first_name || t('common.user', 'User')}
        />
      </div>

      {/* Header with Date Range Selector */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            {t('dashboard.resultsTitle', 'Resultados dos seus anúncios')}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {t('dashboard.resultsSubtitle', 'Quantas pessoas viram, clicaram e compraram no período que você escolher.')}
          </p>
        </div>
        {/* Top row: Plan badge + Date inputs + Actions */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Plan Badge */}
            {plan && (
              <Badge variant="primary" className="px-3 py-1">
                {plan.name}
              </Badge>
            )}

            {/* Project Multi-Select Filter */}
            <div className="relative">
              <button
                onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <span>
                  {selectedProjectIds.length === 0
                    ? t('dashboard.allProjects', 'Todos os Projetos')
                    : selectedProjectIds.length === 1
                      ? activeProjects.find(p => p.id === selectedProjectIds[0])?.name || '1 projeto'
                      : `${selectedProjectIds.length} projetos`}
                </span>
                <ChevronDown className="h-3 w-3" />
              </button>
              {showProjectDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowProjectDropdown(false)} />
                  <div className="absolute left-0 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50 max-h-72 overflow-y-auto">
                    <button
                      onClick={() => { setSelectedProjectIds([]); setShowProjectDropdown(false); }}
                      className={`w-full px-3 py-2 text-sm text-left flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedProjectIds.length === 0 ? 'text-primary-600 font-semibold' : 'text-gray-700 dark:text-gray-200'}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedProjectIds.length === 0 ? 'bg-primary-600 border-primary-600' : 'border-gray-300 dark:border-gray-600'}`}>
                        {selectedProjectIds.length === 0 && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      {t('dashboard.allProjects', 'Todos os Projetos')}
                    </button>
                    <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                    {activeProjects.map(p => {
                      const isSelected = selectedProjectIds.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          onClick={() => {
                            setSelectedProjectIds(prev =>
                              isSelected ? prev.filter(id => id !== p.id) : [...prev, p.id]
                            );
                          }}
                          className="w-full px-3 py-2 text-sm text-left flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300 dark:border-gray-600'}`}>
                            {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                          </div>
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#6366f1' }} />
                          <span className="truncate">{p.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Date Range Inputs */}
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{t('dashboard.dateFrom', 'From')}</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 max-w-[140px] sm:max-w-none"
              />
              <label className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{t('dashboard.dateTo', 'To')}</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 max-w-[140px] sm:max-w-none"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Toggle Values Visibility */}
            <button
              onClick={() => setShowValues(!showValues)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
              title={showValues ? t('dashboard.hideValues') : t('dashboard.showValues')}
            >
              {showValues ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </button>

            {/* Export Dropdown */}
            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="gap-1.5 sm:gap-2"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">{t('dashboard.exportReport', 'Export')}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
              {showExportMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
                  <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                    <button onClick={() => handleExport('pdf')} className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      PDF
                    </button>
                    <button onClick={() => handleExport('xlsx')} className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      Excel (.xlsx)
                    </button>
                    <button onClick={() => handleExport('csv')} className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      CSV
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Dashboard PDF Report Button */}
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowReportSectionsModal(true)}
              disabled={isExportingPDF}
              className="gap-1.5 sm:gap-2"
            >
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">{isExportingPDF ? t('dashboard.generating', 'Gerando...') : t('dashboard.generateReport', 'Gerar Relatório PDF')}</span>
            </Button>

            {/* Guided Tour Button */}
            <button
              onClick={restartTour}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-sm font-medium rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-950/30 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
              title={t('tour.restartTour', 'Restart tour')}
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">{t('tour.restartTour', 'Restart tour')}</span>
            </button>

            {/* Refresh Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={loadDashboardData}
              disabled={isLoading}
              className="gap-1.5 sm:gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t('common.refresh')}</span>
            </Button>

            {/* Last Updated */}
            {lastUpdated && (
              <div className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{formatLastUpdated(lastUpdated)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Preset buttons row */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          {presets.map((preset) => (
            <button
              key={preset.key}
              onClick={() => applyPreset(preset.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                activePreset === preset.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards Grid - 8 cards in 2 rows of 4 */}
      <div data-tour="kpi-cards" className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        {isLoading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <KPICard key={i} title="" value="" loading />
          ))
        ) : (
          kpiCards.map((kpi, index) => (
            <KPICard
              key={index}
              title={kpi.title}
              value={kpi.value}
              change={kpi.change}
              changeLabel={t('dashboard.vsLastPeriod')}
              icon={kpi.icon}
              format={kpi.format}
              sparklineData={kpi.sparkline}
              hideValue={!showValues}
            />
          ))
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        {/* Performance Chart - spans 2 columns */}
        <div data-tour="performance-chart" className="xl:col-span-2">
          <PerformanceChart
            data={chartData?.performance || []}
            metrics={['impressions', 'clicks', 'conversions']}
            title={t('dashboard.performance')}
            loading={isLoading}
            height={350}
          />
        </div>

        {/* Activity Feed */}
        <div data-tour="activity-feed">
          <ActivityFeed activities={activities} loading={isLoading} />
        </div>
      </div>

      {/* Platform Comparison and Upcoming Posts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div data-tour="platform-comparison">
          <PlatformComparison
            data={chartData?.platforms || []}
            loading={isLoading}
            metric="spend"
          />
        </div>
        <UpcomingPosts posts={upcomingPosts} loading={isLoading} />
      </div>

      {/* AI Insights - full width */}
      <div data-tour="ai-insights">
        <AIInsightsPanel
          insights={insights}
          loading={isLoading}
          onRefresh={loadDashboardData}
        />
      </div>

      {/* Top Spending Today + Top Keywords + Top Campaigns */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        {/* Top Spending Today */}
        <Card>
          <CardHeader
            action={
              <Link to="/campaigns">
                <Button variant="ghost" size="sm" className="whitespace-nowrap">
                  {t('common.viewAll')}
                </Button>
              </Link>
            }
          >
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-orange-500" />
              {t('dashboard.topSpendingToday', 'Maiores gastos do dia')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
                ))}
              </div>
            ) : topSpendingToday.length === 0 ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                <DollarSign className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>{t('dashboard.noSpendToday', 'Nenhuma campanha gastou hoje')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {topSpendingToday.map((campaign, index) => (
                  <Link
                    key={campaign.id}
                    to={`/campaigns/${campaign.id}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-gray-400 bg-gray-100 dark:bg-gray-700 rounded flex-shrink-0">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate max-w-[120px] sm:max-w-[160px]">
                          {campaign.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                          {campaign.platform}
                          {campaign.budget_used_pct != null && (
                            <span className={`ml-2 ${campaign.budget_used_pct > 100 ? 'text-red-600' : campaign.budget_used_pct > 80 ? 'text-orange-500' : ''}`}>
                              {campaign.budget_used_pct.toFixed(0)}% do diário
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {new Intl.NumberFormat(undefined, { style: 'currency', currency: campaign.currency || 'BRL' }).format(campaign.spend_today)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {campaign.conversions_today > 0
                          ? `${campaign.conversions_today} conv.`
                          : `${campaign.clicks_today} cliques`}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Search Terms (real queries that triggered ads) */}
        <Card>
          <CardHeader
            action={
              <Link to="/campaigns">
                <Button variant="ghost" size="sm" className="whitespace-nowrap">
                  {t('common.viewAll')}
                </Button>
              </Link>
            }
          >
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-blue-500" />
              {t('dashboard.topSearchTerms', 'Termos mais buscados (30d)')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
                ))}
              </div>
            ) : topSearchTerms.length === 0 ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                <Search className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>{t('dashboard.noSearchTerms', 'Sem dados de busca nos últimos 30 dias')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {topSearchTerms.map((term, index) => (
                  <div
                    key={`${term.search_term}-${index}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-gray-400 bg-gray-100 dark:bg-gray-700 rounded flex-shrink-0">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate max-w-[140px] sm:max-w-[180px]">
                          {term.search_term}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {term.impressions.toLocaleString()} imp · CTR {term.ctr.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {term.clicks}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {term.conversions > 0 ? `${term.conversions} conv.` : t('common.clicks', 'cliques')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Campaigns */}
        <Card>
          <CardHeader
            action={
              <Link to="/campaigns">
                <Button variant="ghost" size="sm" className="whitespace-nowrap">
                  {t('common.viewAll')}
                </Button>
              </Link>
            }
          >
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-500" />
              {t('dashboard.topCampaigns')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
                ))}
              </div>
            ) : topCampaigns.length === 0 ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                <Award className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>{t('common.noData')}</p>
                <Link to="/campaigns/new" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  {t('campaigns.newCampaign')}
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {topCampaigns.map((campaign, index) => (
                  <Link
                    key={campaign.id}
                    to={`/campaigns/${campaign.id}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 flex items-center justify-center text-xs font-bold text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate max-w-[100px] sm:max-w-[140px]">
                          {campaign.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                          {campaign.platform}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {campaign.cpa != null ? (
                        <>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {new Intl.NumberFormat(undefined, { style: 'currency', currency: campaign.currency || 'BRL' }).format(campaign.cpa)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">CPA</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {campaign.total_conversions || 0}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('dashboard.conversions', 'Conversões')}
                          </p>
                        </>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions Grid - Temporarily disabled */}
      {/* <QuickActionsGrid /> */}

      {/* Report Sections Selection Modal */}
      {showReportSectionsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowReportSectionsModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('dashboard.reportSections.title', 'Configurar Relatório')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('dashboard.reportSections.subtitle', 'Selecione as seções que deseja incluir no PDF')}
                </p>
              </div>
              <button
                onClick={() => setShowReportSectionsModal(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Select All */}
            <div className="px-6 pt-4">
              <button
                onClick={toggleAllSections}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  allSectionsSelected
                    ? 'bg-primary-600 border-primary-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  {allSectionsSelected && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('dashboard.reportSections.selectAll', 'Selecionar todas as seções')}
                </span>
              </button>
            </div>

            {/* Sections List */}
            <div className="px-6 py-3 space-y-1">
              {reportSections.map((section) => {
                const isSelected = selectedSections.includes(section.key);
                const Icon = section.icon;
                return (
                  <button
                    key={section.key}
                    onClick={() => toggleSection(section.key)}
                    className={`flex items-start gap-3 w-full px-3 py-3 rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 dark:hover:bg-primary-950/30'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      isSelected
                        ? 'bg-primary-600 border-primary-600'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${isSelected ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300'}`}>
                          {section.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 ml-6">
                        {section.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {selectedSections.length} {t('dashboard.reportSections.of', 'de')} {reportSections.length} {t('dashboard.reportSections.selected', 'selecionadas')}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowReportSectionsModal(false)}
                >
                  {t('common.cancel', 'Cancelar')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleExportDashboardPDF}
                  disabled={selectedSections.length === 0}
                  className="gap-1.5"
                >
                  <Download className="h-4 w-4" />
                  {t('dashboard.reportSections.generate', 'Gerar PDF')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardPage;
